"use client"

import type React from "react"
import { useState, useEffect, useCallback, useRef } from "react"
import { View, StyleSheet } from "react-native"
import { Text, useTheme, ActivityIndicator } from "react-native-paper"
import { GoogleMap, useJsApiLoader, Polyline } from "@react-google-maps/api"
import type { AssetNode, ProjectData, TowerWithCoords } from "../../types/project"
import { MapTypeControl } from "./LineMap/MapTypeControl"
import { MapControls } from "./LineMap/MapControls"
import { TowerMarker } from "./LineMap/TowerMarker"
import Constants from "expo-constants"
import { getRotatedImageUrl } from "../../utils/imageUtils"
import { PropertiesPane } from "./LineMap/PropertiesPane"
import { debugLog } from "../../constants/debug"

// Map configuration constants
const MAP_CONSTANTS = {
    DEFAULT_ZOOM: 10,
    MAX_ZOOM: 20,
    MIN_ZOOM: 3,
    DEFAULT_CENTER: { lat: 0, lng: 0 },
    POLYLINE_COLOR: "#3B82F6",
    POLYLINE_THICKNESS: 3,
    ANIMATION_DURATION: 1000,
    MAP_PADDING: 100,
    SINGLE_TOWER_ZOOM: 20,
    ANIMATION_SPEED: 1500, // Increased for smoother animation
    AERIAL_TOUR_DELAY: 3500, // Increased for better viewing time
    AERIAL_TOUR_ZOOM: 16,
    MARKER_SIZE: { width: 48, height: 48 },
}

// Span data with coordinates
interface SpanWithCoords {
    id: string
    name: string
    startTower: TowerWithCoords
    endTower: TowerWithCoords
    properties: Record<string, any>
    type: string
    parent?: {
        id: string
        name: string
        type: string
    }
}

interface LineMapTabProps {
    selectedNode?: AssetNode | null
    projectData?: ProjectData | null
    onNodeSelect?: (node: AssetNode) => void // Add this prop to handle selection from map to tree
}

export const LineMapTab: React.FC<LineMapTabProps> = ({ selectedNode, projectData, onNodeSelect }) => {
    const theme = useTheme()
    const mapRef = useRef<google.maps.Map | null>(null)
    const [mapType, setMapType] = useState<string>("hybrid")
    const [towers, setTowers] = useState<TowerWithCoords[]>([])
    const [spans, setSpans] = useState<SpanWithCoords[]>([])
    const [hoveredTower, setHoveredTower] = useState<TowerWithCoords | null>(null)
    const [hoveredSpan, setHoveredSpan] = useState<SpanWithCoords | null>(null)
    const [selectedTower, setSelectedTower] = useState<TowerWithCoords | null>(null)
    const [selectedSpan, setSelectedSpan] = useState<SpanWithCoords | null>(null)
    const [isAerialViewActive, setIsAerialViewActive] = useState(false)
    const [currentAerialIndex, setCurrentAerialIndex] = useState(0)
    const aerialViewTimerRef = useRef<NodeJS.Timeout | null>(null)
    const zoomTimerRef = useRef<NodeJS.Timeout | null>(null)
    const aerialIndexRef = useRef<number>(0)
    const markerIconsRef = useRef<Record<string, google.maps.Icon>>({})
    const [propertiesPaneCollapsed, setPropertiesPaneCollapsed] = useState(false)
    const [currentZoom, setCurrentZoom] = useState<number>(MAP_CONSTANTS.DEFAULT_ZOOM)
    // Add state for label visibility
    const [showLabels, setShowLabels] = useState<boolean>(true)

    // Add a ref to track if selection is coming from the map
    const selectionFromMapRef = useRef<boolean>(false)

    // Load Google Maps API
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: Constants.expoConfig?.extra?.GOOGLE_MAPS_API_KEY || "",
    })

    // Extract tower and span data from project data
    useEffect(() => {
        if (!projectData) return

        // First pass: collect all unique towers with their data
        const towerMap = new Map<string, TowerWithCoords>()
        const spanMap = new Map<string, SpanWithCoords>()
        const sectionTowersMap = new Map<string, string[]>() // Map of section ID to list of tower names
        const sectionSpansMap = new Map<string, string[]>() // Map of section ID to list of span names

        // Function to recursively extract towers from the project data
        const collectTowers = (node: AssetNode, parent?: { id: string; name: string; type: string }) => {
            if (node.type === "tower") {
                const lat = Number.parseFloat(node.properties.latitude || node.properties.Latitude || "0")
                const lng = Number.parseFloat(node.properties.longitude || node.properties.Longitude || "0")

                if (!isNaN(lat) && !isNaN(lng)) {
                    // Add or update the tower in our map
                    towerMap.set(node.name, {
                        id: node.name,
                        name: node.name,
                        lat,
                        lng,
                        properties: node.properties,
                        type: node.type,
                        parent,
                    })

                    // Track which towers belong to which section
                    if (parent && parent.type === "section") {
                        if (!sectionTowersMap.has(parent.id)) {
                            sectionTowersMap.set(parent.id, [])
                        }
                        sectionTowersMap.get(parent.id)?.push(node.name)
                    }
                }
            }

            // Process children recursively
            if (node.children) {
                node.children.forEach((child) => {
                    collectTowers(child, node.type === "section" ? { id: node.name, name: node.name, type: node.type } : parent)
                })
            }
        }

        // Collect spans using the same approach as towers
        const collectSpans = (node: AssetNode, parent?: { id: string; name: string; type: string }) => {
            if (node.type === "span") {
                const startTowerName =
                    node.properties["Start Location"] || node.properties.summary?.["Basic Details"]?.["Start Tower"]?.value
                const endTowerName =
                    node.properties["End Location"] || node.properties.summary?.["Basic Details"]?.["End Tower"]?.value

                // Find the corresponding tower objects
                const startTower = towerMap.get(startTowerName)
                const endTower = towerMap.get(endTowerName)

                // Only create a span if we have both towers
                if (startTower && endTower) {
                    // Add the span to our map using the same structure as towers
                    spanMap.set(node.name, {
                        id: node.name,
                        name: node.name,
                        startTower: startTower,
                        endTower: endTower,
                        properties: node.properties,
                        type: node.type,
                        parent,
                    })

                    // Track which spans belong to which section
                    if (parent && parent.type === "section") {
                        if (!sectionSpansMap.has(parent.id)) {
                            sectionSpansMap.set(parent.id, [])
                        }
                        sectionSpansMap.get(parent.id)?.push(node.name)
                    }
                }
            }

            // Process children recursively
            if (node.children) {
                node.children.forEach((child) => {
                    collectSpans(child, node.type === "section" ? { id: node.name, name: node.name, type: node.type } : parent)
                })
            }
        }

        // Start extraction from the root node
        if (projectData.Lines) {
            // First collect all towers
            collectTowers(projectData.Lines)

            // Then collect spans (which need towers to be collected first)
            collectSpans(projectData.Lines)

            // Convert maps to arrays and set state
            setTowers(Array.from(towerMap.values()))
            setSpans(Array.from(spanMap.values()))
        }
    }, [projectData])

    // Function to animate map to show all relevant towers
    const animateToTowers = useCallback(
        (towersToShow: TowerWithCoords[]) => {
            if (!mapRef.current || towersToShow.length === 0 || !isLoaded) return

            // Clear any pending zoom animations
            if (zoomTimerRef.current) {
                clearTimeout(zoomTimerRef.current)
                zoomTimerRef.current = null
            }

            if (towersToShow.length === 1) {
                // For single tower, animate to it and then zoom in
                const tower = towersToShow[0]

                // First animate to the tower's position with smooth easing
                mapRef.current.panTo(
                    { lat: tower.lat, lng: tower.lng },
                    {
                        duration: MAP_CONSTANTS.ANIMATION_SPEED,
                        easing: (x: number) => {
                            // Smooth easing function
                            return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2
                        },
                    },
                )

                // Get current zoom level
                const currentZoom = mapRef.current.getZoom() || MAP_CONSTANTS.DEFAULT_ZOOM
                const targetZoom = MAP_CONSTANTS.SINGLE_TOWER_ZOOM
                const steps = 20 // Increased for smoother zoom
                const stepDelay = MAP_CONSTANTS.ANIMATION_SPEED / steps
                const zoomIncrement = (targetZoom - currentZoom) / steps

                // Animate zoom level gradually
                let step = 0
                const animateZoom = () => {
                    if (step < steps && mapRef.current) {
                        step++
                        const newZoom = currentZoom + zoomIncrement * step
                        mapRef.current.setZoom(newZoom)

                        if (step < steps) {
                            zoomTimerRef.current = setTimeout(animateZoom, stepDelay)
                        }
                    }
                }

                // Start zoom animation after a short delay to let the pan animation start
                zoomTimerRef.current = setTimeout(animateZoom, 100)
            } else {
                // Create bounds that include all towers
                const bounds = new window.google.maps.LatLngBounds()
                towersToShow.forEach((tower) => {
                    bounds.extend({ lat: tower.lat, lng: tower.lng })
                })

                // Fit bounds with padding and animation
                mapRef.current.fitBounds(bounds, {
                    padding: MAP_CONSTANTS.MAP_PADDING,
                    duration: 1000, // Add duration for smoother fit
                })
            }
        },
        [isLoaded],
    )

    // Handle selected node changes from TreeView
    useEffect(() => {
        // Skip if selection is coming from the map to prevent recursion
        if (selectionFromMapRef.current) {
            selectionFromMapRef.current = false
            return
        }

        if (!selectedNode || !isLoaded || towers.length === 0) return

        // Stop any ongoing aerial view
        if (aerialViewTimerRef.current) {
            clearTimeout(aerialViewTimerRef.current)
            aerialViewTimerRef.current = null
            setIsAerialViewActive(false)
        }

        // Clear any pending zoom animations
        if (zoomTimerRef.current) {
            clearTimeout(zoomTimerRef.current)
            zoomTimerRef.current = null
        }

        // Filter towers based on selected node
        let relevantTowers: TowerWithCoords[] = []

        if (selectedNode.type === "line") {
            // All towers are relevant
            relevantTowers = towers

            // Clear tower and span selection for line nodes
            setSelectedTower(null)
            setSelectedSpan(null)
        } else if (selectedNode.type === "section") {
            // Only towers in this section
            relevantTowers = towers.filter((tower) => tower.parent?.id === selectedNode.name)

            // Clear tower and span selection for section nodes
            setSelectedTower(null)
            setSelectedSpan(null)
        } else if (selectedNode.type === "tower") {
            // Only the selected tower
            relevantTowers = towers.filter((tower) => tower.name === selectedNode.name)

            // Update the selected tower state to show properties in the PropertiesPane
            const matchingTower = towers.find((tower) => tower.name === selectedNode.name)
            if (matchingTower) {
                setSelectedTower(matchingTower)
                setSelectedSpan(null)
                // Also update hovered state to match selected state
                setHoveredTower(matchingTower)
                setHoveredSpan(null)
            }
        } else if (selectedNode.type === "span") {
            // Find the span and its towers
            const matchingSpan = spans.find((span) => span.name === selectedNode.name)
            if (matchingSpan) {
                relevantTowers = [matchingSpan.startTower, matchingSpan.endTower]

                // Update the selected span state to show properties in the PropertiesPane
                setSelectedSpan(matchingSpan)
                setSelectedTower(null)
                // Also update hovered state to match selected state
                setHoveredSpan(matchingSpan)
                setHoveredTower(null)
            }
        }

        if (relevantTowers.length > 0 && mapRef.current) {
            // Animate to the relevant towers
            animateToTowers(relevantTowers)
        }
    }, [selectedNode, towers, spans, isLoaded, animateToTowers])

    // Toggle map labels
    const toggleLabels = useCallback(() => {
        if (mapRef.current) {
            // Store current center and zoom
            const currentCenter = mapRef.current.getCenter()
            const currentZoom = mapRef.current.getZoom()

            // Update state
            setShowLabels((prev) => !prev)

            // Apply the style change
            const styles = !showLabels
                ? []
                : [
                    {
                        featureType: "all",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }],
                    },
                ]

            mapRef.current.setOptions({ styles })

            // Restore center and zoom after a short delay to ensure styles are applied
            setTimeout(() => {
                if (mapRef.current && currentCenter) {
                    mapRef.current.setCenter(currentCenter)
                    if (currentZoom) mapRef.current.setZoom(currentZoom)
                }
            }, 10)
        } else {
            // Just update state if map isn't loaded yet
            setShowLabels((prev) => !prev)
        }
    }, [showLabels])

    // Apply label visibility when map is loaded or showLabels changes
    useEffect(() => {
        if (isLoaded && mapRef.current) {
            const styles = !showLabels
                ? [
                    {
                        featureType: "all",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }],
                    },
                ]
                : []

            mapRef.current.setOptions({ styles })
        }
    }, [isLoaded, showLabels])

    // Handle map load
    const onMapLoad = useCallback(
        (map: google.maps.Map) => {
            mapRef.current = map

            // Set initial zoom level
            setCurrentZoom(map.getZoom() || MAP_CONSTANTS.DEFAULT_ZOOM)

            // Add zoom change listener
            map.addListener("zoom_changed", () => {
                setCurrentZoom(map.getZoom() || MAP_CONSTANTS.DEFAULT_ZOOM)
            })

            // Ensure map type is set correctly
            if (mapType === "hybrid") {
                map.setMapTypeId(google.maps.MapTypeId.HYBRID)
            }

            // Apply label visibility setting
            const styles = !showLabels
                ? [
                    {
                        featureType: "all",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }],
                    },
                ]
                : []

            map.setOptions({ styles })

            // If there's a selected node, animate to it
            if (selectedNode && towers.length > 0) {
                let relevantTowers: TowerWithCoords[] = []

                if (selectedNode.type === "line") {
                    relevantTowers = towers
                } else if (selectedNode.type === "section") {
                    relevantTowers = towers.filter((tower) => tower.parent?.id === selectedNode.name)
                } else if (selectedNode.type === "tower") {
                    relevantTowers = towers.filter((tower) => tower.name === selectedNode.name)

                    // Also update the selected tower state
                    const matchingTower = towers.find((tower) => tower.name === selectedNode.name)
                    if (matchingTower) {
                        setSelectedTower(matchingTower)
                        setSelectedSpan(null)
                    }
                } else if (selectedNode.type === "span") {
                    const matchingSpan = spans.find((span) => span.name === selectedNode.name)
                    if (matchingSpan) {
                        relevantTowers = [matchingSpan.startTower, matchingSpan.endTower]

                        // Also update the selected span state
                        setSelectedSpan(matchingSpan)
                        setSelectedTower(null)
                    }
                }

                if (relevantTowers.length > 0) {
                    animateToTowers(relevantTowers)
                }
            } else if (towers.length > 0) {
                // If no selected node but we have towers, show all towers
                animateToTowers(towers)
            }
        },
        [selectedNode, towers, spans, animateToTowers, mapType, showLabels],
    )

    const handleMapTypeChange = useCallback((type: string) => {
        setMapType(type)
    }, [])

    const handleTowerMouseOver = useCallback((tower: TowerWithCoords) => {
        setHoveredTower(tower)
    }, [])

    const handleTowerMouseOut = useCallback(() => {
        // Only clear hover if it's not the selected tower
        if (selectedTower?.name !== hoveredTower?.name) {
            setHoveredTower(null)
        }
    }, [selectedTower, hoveredTower])

    const handleSpanMouseOver = useCallback((span: SpanWithCoords) => {
        setHoveredSpan(span)
    }, [])

    const handleSpanMouseOut = useCallback(() => {
        // Only clear hover if it's not the selected span
        if (selectedSpan?.name !== hoveredSpan?.name) {
            setHoveredSpan(null)
        }
    }, [selectedSpan, hoveredSpan])

    // Handle tower click - notify TreeView
    const handleTowerClick = useCallback(
        (tower: TowerWithCoords) => {
            debugLog("Tower clicked", tower.name)
            setSelectedTower(tower)
            setSelectedSpan(null)
            // Update hovered state to match selected state
            setHoveredTower(tower)
            setHoveredSpan(null)

            // Find the corresponding node in the tree and notify parent
            if (onNodeSelect && projectData?.Lines) {
                // Set flag to prevent recursion
                selectionFromMapRef.current = true

                // Find the tower node in the tree
                const findTowerNode = (node: AssetNode): AssetNode | null => {
                    if (node.type === "tower" && node.name === tower.name) {
                        return node
                    }

                    if (node.children) {
                        for (const child of node.children) {
                            const found = findTowerNode(child)
                            if (found) return found
                        }
                    }

                    return null
                }

                const towerNode = findTowerNode(projectData.Lines)
                if (towerNode) {
                    onNodeSelect(towerNode)
                }
            }
        },
        [onNodeSelect, projectData],
    )

    // Handle span click - notify TreeView
    const handleSpanClick = useCallback(
        (span: SpanWithCoords) => {
            debugLog("Span clicked", span.name)
            setSelectedSpan(span)
            setSelectedTower(null)
            // Update hovered state to match selected state
            setHoveredSpan(span)
            setHoveredTower(null)

            // Find the corresponding node in the tree and notify parent
            if (onNodeSelect && projectData?.Lines) {
                // Set flag to prevent recursion
                selectionFromMapRef.current = true

                // Find the span node in the tree
                const findSpanNode = (node: AssetNode): AssetNode | null => {
                    if (node.type === "span" && node.name === span.name) {
                        return node
                    }

                    if (node.children) {
                        for (const child of node.children) {
                            const found = findSpanNode(child)
                            if (found) return found
                        }
                    }

                    return null
                }

                const spanNode = findSpanNode(projectData.Lines)
                if (spanNode) {
                    onNodeSelect(spanNode)
                }
            }
        },
        [onNodeSelect, projectData],
    )

    const stopAerialView = useCallback(() => {
        if (aerialViewTimerRef.current) {
            clearTimeout(aerialViewTimerRef.current)
            aerialViewTimerRef.current = null
        }
        setIsAerialViewActive(false)
    }, [])

    const startAerialView = useCallback(() => {
        if (towers.length === 0 || !isLoaded || !mapRef.current) return

        // Set aerial view as active
        setIsAerialViewActive(true)

        // Reset the index
        aerialIndexRef.current = 0
        setCurrentAerialIndex(0)

        // Set initial zoom level with animation
        mapRef.current.setZoom(MAP_CONSTANTS.AERIAL_TOUR_ZOOM)

        // Function to move to the next tower
        const moveToNextTower = () => {
            if (!mapRef.current) return

            // Get the next tower index
            const nextIndex = (aerialIndexRef.current + 1) % towers.length
            const nextTower = towers[nextIndex]

            // Update the current index
            aerialIndexRef.current = nextIndex
            setCurrentAerialIndex(nextIndex)

            // Animate to the next tower with enhanced smooth motion
            mapRef.current.panTo(
                { lat: nextTower.lat, lng: nextTower.lng },
                {
                    duration: 2500, // Increased for smoother animation
                    easing: (x: number) => {
                        // Use a more sophisticated easing function
                        return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2 // Ease in-out cubic
                    },
                },
            )

            // Schedule the next tower visit if aerial view is still active
            aerialViewTimerRef.current = setTimeout(moveToNextTower, MAP_CONSTANTS.AERIAL_TOUR_DELAY)
        }

        // Start with the first tower
        const firstTower = towers[0]
        mapRef.current.panTo(
            { lat: firstTower.lat, lng: firstTower.lng },
            {
                duration: 1500,
                easing: (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2),
            },
        )

        // Schedule the first move after a delay
        aerialViewTimerRef.current = setTimeout(moveToNextTower, 3000)
    }, [towers, isLoaded])

    const toggleAerialView = useCallback(() => {
        if (isAerialViewActive) {
            stopAerialView()
        } else {
            startAerialView()
        }
    }, [isAerialViewActive, startAerialView, stopAerialView])

    const getTowerIcon = useCallback(
        async (angle: number) => {
            try {
                // Calculate icon size based on zoom level with enhanced scaling
                const zoomLevel = currentZoom || MAP_CONSTANTS.DEFAULT_ZOOM
                const minZoom = MAP_CONSTANTS.MIN_ZOOM
                const maxZoom = MAP_CONSTANTS.MAX_ZOOM

                // Define reference points for scaling
                const referenceZoom = 10 // This is our reference zoom level (default)
                const minSize = 6 // Minimum size at lowest zoom
                const defaultSize = 12 // Size at reference zoom (50% of original 24)
                const maxSize = 36 // Maximum size at highest zoom (50% larger than original max)

                // Calculate size using a more aggressive exponential scaling
                // This will make icons grow more significantly at higher zoom levels
                let iconSize

                if (zoomLevel <= referenceZoom) {
                    // Linear scaling for zoom levels below reference
                    const zoomRatio = (zoomLevel - minZoom) / (referenceZoom - minZoom)
                    iconSize = minSize + zoomRatio * (defaultSize - minSize)
                } else {
                    // Exponential scaling for zoom levels above reference
                    // This makes icons grow more dramatically as you zoom in
                    const zoomRatio = (zoomLevel - referenceZoom) / (maxZoom - referenceZoom)
                    const exponentialFactor = Math.pow(zoomRatio, 1.5) // Exponential growth
                    iconSize = defaultSize + exponentialFactor * (maxSize - defaultSize)
                }

                // Round to nearest integer
                iconSize = Math.round(iconSize)

                // Create a cache key that includes both angle and size
                const cacheKey = `${angle}-${iconSize}`

                // Check if we already have this icon in the ref
                if (markerIconsRef.current[cacheKey]) {
                    return markerIconsRef.current[cacheKey]
                }

                // milky white tower url
                const imageUrl = "https://res.cloudinary.com/dfdf512sx/image/upload/v1742091765/tower_milkywhite.png"
                //blue tower url
                //const imageUrl = "https://res.cloudinary.com/dfdf512sx/image/upload/v1742091765/tower_milkywhite_vwck1u.pngs://res.cloudinary.com/dfdf512sx/image/upload/v1741953747/tower_blue.png"

                const rotatedIconUrl = await getRotatedImageUrl(imageUrl, angle)

                if (!rotatedIconUrl) {
                    console.error(`Failed to get rotated URL for angle: ${angle}`)
                    return null
                }

                const icon = {
                    url: rotatedIconUrl,
                    scaledSize: new window.google.maps.Size(iconSize, iconSize),
                    anchor: new window.google.maps.Point(iconSize / 2, iconSize / 2),
                }

                // Cache the icon in the ref
                markerIconsRef.current[cacheKey] = icon
                return icon
            } catch (error) {
                console.error(`Failed to load tower icon for angle ${angle}:`, error)

                // Return a fallback icon in case of error
                const zoomLevel = currentZoom || MAP_CONSTANTS.DEFAULT_ZOOM
                const iconSize = Math.max(8, Math.min(24, Math.round(zoomLevel)))

                return {
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                        `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"></path></svg>`,
                    )}`,
                    scaledSize: new window.google.maps.Size(iconSize, iconSize),
                    anchor: new window.google.maps.Point(iconSize / 2, iconSize / 2),
                }
            }
        },
        [currentZoom],
    )

    const getTowerRotation = useCallback((tower: TowerWithCoords): number => {
        return tower.properties["TowerIconAngle"] || 0
    }, [])

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (aerialViewTimerRef.current) {
                clearTimeout(aerialViewTimerRef.current)
            }
            if (zoomTimerRef.current) {
                clearTimeout(zoomTimerRef.current)
            }
        }
    }, [])

    // Render loading state
    if (loadError) {
        return (
            <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
                <Text style={{ color: theme.colors.error }}>Error loading Google Maps: {loadError.message}</Text>
            </View>
        )
    }

    if (!isLoaded) {
        return (
            <View
                style={[
                    styles.container,
                    { backgroundColor: theme.colors.background, justifyContent: "center", alignItems: "center" },
                ]}
            >
                <ActivityIndicator size="large" color={theme.colors.primary} />
                <Text style={{ color: theme.colors.onSurface, marginTop: 16 }}>Loading Google Maps...</Text>
            </View>
        )
    }

    // Declare google variable
    const google = typeof window !== "undefined" && window.google

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
            {isLoaded && typeof window !== "undefined" && window.google && (
                <GoogleMap
                    mapContainerStyle={styles.mapContainer}
                    center={MAP_CONSTANTS.DEFAULT_CENTER}
                    zoom={MAP_CONSTANTS.DEFAULT_ZOOM}
                    mapTypeId={mapType}
                    options={{
                        mapTypeControl: false,
                        fullscreenControl: false,
                        streetViewControl: true,
                        zoomControl: true,
                        zoomControlOptions: {
                            position: google.maps.ControlPosition.RIGHT_CENTER,
                        },
                        // Disable the built-in label toggle by restricting map types
                        mapTypeIds: ["roadmap", "satellite", "hybrid", "terrain"],
                        styles: !showLabels
                            ? [
                                {
                                    featureType: "all",
                                    elementType: "labels",
                                    stylers: [{ visibility: "off" }],
                                },
                            ]
                            : [],
                    }}
                    onLoad={onMapLoad}
                >
                    {/* Render tower markers */}
                    {towers.map((tower) => {
                        const angle = getTowerRotation(tower)
                        const isSelected = selectedTower?.name === tower.name

                        return (
                            <TowerMarker
                                key={tower.id}
                                tower={tower}
                                angle={angle}
                                currentZoom={currentZoom}
                                onMouseOver={handleTowerMouseOver}
                                onMouseOut={handleTowerMouseOut}
                                onClick={handleTowerClick}
                                getTowerIcon={getTowerIcon}
                                isSelected={isSelected}
                            />
                        )
                    })}

                    {/* Always render all span polylines */}
                    {spans.map((span) => {
                        const isSelected = selectedSpan?.name === span.name

                        return (
                            <Polyline
                                key={span.id}
                                path={[
                                    { lat: span.startTower.lat, lng: span.startTower.lng },
                                    { lat: span.endTower.lat, lng: span.endTower.lng },
                                ]}
                                options={{
                                    strokeColor: isSelected ? "#FF0000" : MAP_CONSTANTS.POLYLINE_COLOR, // Highlight selected spans
                                    strokeWeight: isSelected ? MAP_CONSTANTS.POLYLINE_THICKNESS + 2 : MAP_CONSTANTS.POLYLINE_THICKNESS,
                                    strokeOpacity: isSelected ? 1.0 : 0.8,
                                    clickable: true,
                                }}
                                onMouseOver={() => handleSpanMouseOver(span)}
                                onMouseOut={handleSpanMouseOut}
                                onClick={() => handleSpanClick(span)}
                            />
                        )
                    })}

                    {/* Map controls */}
                    <MapTypeControl onChange={handleMapTypeChange} currentType={mapType} />
                    <MapControls
                        onAerialViewToggle={toggleAerialView}
                        isAerialViewActive={isAerialViewActive}
                        onShowAllTowers={() => animateToTowers(towers)}
                        onToggleLabels={toggleLabels}
                        showLabels={showLabels}
                    />
                </GoogleMap>
            )}
            {/* Add PropertiesPane after the GoogleMap component */}
            <PropertiesPane
                isCollapsed={propertiesPaneCollapsed}
                onToggle={() => setPropertiesPaneCollapsed(!propertiesPaneCollapsed)}
                hoveredTower={hoveredTower || selectedTower} // Show properties for either hovered or selected tower
                hoveredSpan={hoveredSpan || selectedSpan} // Show properties for either hovered or selected span
                projectData={projectData}
            />
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        height: "100%",
    },
    mapContainer: {
        width: "100%",
        height: "100%",
    },
})

